import { PlayerWithRels, Search, SearchResult, TeamWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import MiniSearch from 'minisearch';
import { playerIdMappingAtom, rawPlayersWithCacheFallbackAtom } from '@/atoms/queries/players';
import {
  buildCycleWithRelsLookup,
  getTeamCompetitionName,
  teamIdMappingAtom,
  unwrappedRawTeamsAtom,
} from '@/atoms/queries/teams';
import { MAX_SEARCH_RESULTS_COUNT, MIN_QUERY_LENGTH, SEARCH_INDEX_CHUNK_SIZE } from '@/consts/search';
import { FUZZY_SEARCH_OPTIONS, rankByExactMatch } from '@/utils/search';
import { lastToken, searchFormattedString } from '@/utils/string';
import { searchValueAtom } from '../search/search';
import { competitionLookupAtom } from './competitions';
import { rawCyclesAtom } from './cycles';
import { seasonLookupAtom } from './seasons';
import { userNavOverridesAtom } from './user';

type SearchableData = (PlayerWithRels | TeamWithRels) & {
  searchName: string;
  searchNickname: string;
  searchDisplayName: string;
  searchLastName: string;
};

export interface SearchIndex {
  miniSearch: MiniSearch;
  byId: Map<string, SearchableData>;
}

const SEARCH_FIELDS = ['searchLastName', 'searchDisplayName', 'searchName', 'searchNickname'] as const;
// Surname-first: most users search by last name, so searchLastName is boosted highest.
// Nickname weight (1) is provisional — common nicknames may warrant a higher value once verified against real data.
const SEARCH_FIELD_BOOSTS = { searchLastName: 3, searchDisplayName: 2, searchName: 1.5, searchNickname: 1 };

const namespaceId = (item: PlayerWithRels | TeamWithRels) =>
  'player_id' in item ? `player-${item.player_id}` : `team-${item.team_id}`;

const toSearchResult = (item: SearchableData): SearchResult => {
  const isPlayer = 'player_id' in item;
  return {
    type: isPlayer ? 'player' : 'team',
    age: isPlayer ? item.age : null,
    id: isPlayer ? item.player_id : item.team_id,
    name: item.name,
    nickname: item.nickname,
    code: (isPlayer ? item.nationality_area_code : item.area_code) || '',
    gender: item.gender,
    ...(isPlayer
      ? {
          clubName: item.current_team?.name ?? null,
          yearOfBirth: item.date_of_birth ? new Date(item.date_of_birth).getFullYear() : null,
        }
      : { competitionName: item.competition_name ?? null }),
  };
};

export const toSearchableData = (item: PlayerWithRels | TeamWithRels): SearchableData => {
  const searchDisplayName = searchFormattedString('display_name' in item ? item.display_name : item.name);
  return {
    ...item,
    searchName: searchFormattedString(item.name),
    searchNickname: searchFormattedString(item.nickname || ''),
    searchDisplayName,
    searchLastName: lastToken(searchDisplayName),
  };
};

const combinedSearchDataAtom = atom(async get => {
  const [players, teams, rawCycles, competitionLookup, seasonLookup] = await Promise.all([
    get(rawPlayersWithCacheFallbackAtom),
    get(unwrappedRawTeamsAtom),
    get(rawCyclesAtom),
    get(competitionLookupAtom),
    get(seasonLookupAtom),
  ]);
  const cycleWithRelsById = buildCycleWithRelsLookup(rawCycles.data, competitionLookup, seasonLookup);
  const teamsWithCompetition = teams.map(team => ({
    ...team,
    competition_name: getTeamCompetitionName(team.cycle_ids, cycleWithRelsById),
  }));

  return [...players, ...teamsWithCompetition].map(toSearchableData);
});

export const buildSearchIndex = async (searchData: SearchableData[]): Promise<SearchIndex> => {
  const miniSearch = new MiniSearch({
    idField: 'id',
    fields: [...SEARCH_FIELDS],
    searchOptions: { boost: SEARCH_FIELD_BOOSTS, ...FUZZY_SEARCH_OPTIONS },
  });

  const byId = new Map<string, SearchableData>();
  const documents = searchData.map(item => {
    const id = namespaceId(item);
    byId.set(id, item);
    return {
      id,
      searchName: item.searchName,
      searchNickname: item.searchNickname,
      searchDisplayName: item.searchDisplayName,
      searchLastName: item.searchLastName,
    };
  });

  await miniSearch.addAllAsync(documents, { chunkSize: SEARCH_INDEX_CHUNK_SIZE });
  return { miniSearch, byId };
};

const searchIndexAtom = atom(async get => buildSearchIndex(await get(combinedSearchDataAtom)));

export const calculateSearchResults = (
  query: string,
  { miniSearch, byId }: SearchIndex,
  playerIdMapping: Record<number, number>,
  teamIdMapping: Record<number, number>,
  playerSectionNavOverride: boolean,
  teamSectionNavOverride: boolean,
): Search => {
  const byPlayerAndTeam = (results: Search, { id }: { id: string }) => {
    const item = byId.get(id);
    if (item) {
      const isPlayer = 'player_id' in item;
      const override = isPlayer ? playerSectionNavOverride : teamSectionNavOverride;
      const mapping = isPlayer ? playerIdMapping : teamIdMapping;
      const result = toSearchResult(item);
      const resultWithNewId = { ...result, id: override ? mapping[result.id] : result.id };
      // Guard: when override is true, mapping[result.id] is undefined for any player/team not in the IQ mapping.
      if (resultWithNewId.id === undefined) return results;
      (isPlayer ? results.player : results.team).push(resultWithNewId);
    }
    return results;
  };

  const { player, team } = rankByExactMatch(query, miniSearch.search(searchFormattedString(query))).reduce(
    byPlayerAndTeam,
    { player: [], team: [] },
  );

  // Cap each type independently so a flood of player matches (e.g. 10 Brazilians named "Everton")
  // cannot bury the team result ("Everton FC"). Per-type cap is intentional, not a bug.
  return {
    player: player.slice(0, MAX_SEARCH_RESULTS_COUNT),
    team: team.slice(0, MAX_SEARCH_RESULTS_COUNT),
  };
};

const searchDataBundleAtom = atom(async get => {
  const [searchIndex, playerIdMapping, teamIdMapping, userNavOverrides] = await Promise.all([
    get(searchIndexAtom),
    get(playerIdMappingAtom),
    get(teamIdMappingAtom),
    get(userNavOverridesAtom),
  ]);
  return { searchIndex, playerIdMapping, teamIdMapping, userNavOverrides };
});

const unwrappedSearchDataBundleAtom = unwrap(searchDataBundleAtom);

export const searchQueryResultsAtom = atom<Search & { isLoading?: boolean }>(get => {
  const query = get(searchValueAtom).trim();
  // Deliberately guard before reading the bundle: the ~130k-doc index is built lazily on first
  // input rather than eagerly on page load, so users who never search never pay for it. The
  // first keystroke's build cost is absorbed by the chunked async build and the isLoading state.
  if (query.length < MIN_QUERY_LENGTH) return { player: [], team: [] };

  const bundle = get(unwrappedSearchDataBundleAtom);
  if (!bundle) return { player: [], team: [], isLoading: true };

  return calculateSearchResults(
    query,
    bundle.searchIndex,
    bundle.playerIdMapping,
    bundle.teamIdMapping,
    bundle.userNavOverrides.player,
    bundle.userNavOverrides.team,
  );
});

// The field's loading spinner reads this, so a failed search index shows in the results panel instead of
// unmounting the field.
export const isSearchQueryLoadingAtom = atom(get => {
  try {
    return get(searchQueryResultsAtom).isLoading ?? false;
  } catch {
    return false;
  }
});
